import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useClubPermissions } from '../hooks/useClubPermissions';
import { SeasonProvider } from '../context/SeasonContext';
import { createSession as createLiveSession } from '../services/sessionService';
import {
  startScheduledClubSession,
  updateSession,
  updateSessionStatus,
  cancelSession,
  deleteSession,
} from '../services/sessionManagementService';
import CustomSessionConfigModal from '../components/session/CustomSessionConfigModal';
import Button from '../components/common/Button';
import { BackButton } from '../components/common';
import { Heading, Text } from '../components/common/Typography';
import { Card } from '../components/common/Card';
import { navigateBack } from '../utils/navigationUtils';
import { useClubSessionsHub } from '../hooks/useClubSessionsHub';
import SessionsTodayWork from '../components/sessions/hub/SessionsTodayWork';
import SessionsPageShell from '../components/sessions/hub/SessionsPageShell';
import ClubLogo from '../components/clubs/ClubLogo';

const SessionStartPage = ({ currentUser }) => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [showStartNowModal, setShowStartNowModal] = useState(false);
  const [startNowLoading, setStartNowLoading] = useState(false);
  const [mutationError, setMutationError] = useState(null);

  const {
    club: selectedClub,
    role: clubRole,
    loading: clubLoading,
    hasPermission,
    error: clubError,
  } = useClubPermissions(clubId, currentUser);

  const {
    upcoming,
    todayWork,
    loading,
    error: hubError,
    refresh: refreshHubSessions,
  } = useClubSessionsHub(clubId, { scope: 'start' });

  const isAdmin =
    currentUser?.role === 'admin' ||
    currentUser?.role === 'super_admin' ||
    clubRole === 'host' ||
    clubRole === 'admin' ||
    clubRole === 'owner';

  const error = mutationError || hubError;

  const todayWorkSessionIds = useMemo(
    () => new Set((todayWork || []).map((item) => item.session?.id)),
    [todayWork]
  );

  const otherPlannedItems = useMemo(
    () =>
      (upcoming || [])
        .filter((session) => !todayWorkSessionIds.has(session.id))
        .map((session) => ({
          type: 'startable',
          session,
          label: session.name,
          action: 'start',
        })),
    [upcoming, todayWorkSessionIds]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('quick') === '1' && selectedClub && isAdmin) {
      setShowStartNowModal(true);
    }
  }, [location.search, selectedClub, isAdmin]);

  const clearQuickStartQueryParam = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('quick') !== '1') return;
    params.delete('quick');
    const search = params.toString();
    navigate(`${location.pathname}${search ? `?${search}` : ''}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const handleCloseStartNowModal = useCallback(() => {
    setShowStartNowModal(false);
    clearQuickStartQueryParam();
  }, [clearQuickStartQueryParam]);

  const handleStartNow = async (sessionData) => {
    if (!selectedClub?.id) return;
    setStartNowLoading(true);
    setMutationError(null);
    try {
      const newSession = await createLiveSession(sessionData);
      setShowStartNowModal(false);
      clearQuickStartQueryParam();
      navigate(`/club/${selectedClub.id}/session/${newSession.id}`);
    } catch (err) {
      console.error('Error starting session:', err);
      setMutationError('Failed to start session. Please try again.');
    } finally {
      setStartNowLoading(false);
    }
  };

  const handleStartPlanned = async (sessionId) => {
    try {
      const { session: activeSession } = await startScheduledClubSession(sessionId, currentUser.id);
      navigate(`/club/${clubId}/session/${activeSession.id}`);
    } catch (err) {
      console.error('Error starting session:', err);
      setMutationError(err.message || 'Failed to start session');
    }
  };

  const handleEditSession = async (sessionId, updates) => {
    try {
      await updateSession(sessionId, updates);
      await refreshHubSessions();
      setMutationError(null);
    } catch (err) {
      console.error('Error updating session:', err);
      setMutationError(err.message || 'Failed to update session');
    }
  };

  const handleUpdateSessionStatus = async (sessionId, newStatus) => {
    try {
      await updateSessionStatus(sessionId, newStatus);
      await refreshHubSessions();
      setMutationError(null);
    } catch (err) {
      console.error('Error updating session status:', err);
      setMutationError(err.message || 'Failed to update session status');
    }
  };

  const handleCancelSession = async (sessionId) => {
    try {
      await cancelSession(sessionId);
      await refreshHubSessions();
      setMutationError(null);
    } catch (err) {
      console.error('Error cancelling session:', err);
      setMutationError(err.message || 'Failed to cancel session');
    }
  };

  const handleDeleteSession = async (sessionOrId) => {
    const sessionId = typeof sessionOrId === 'object' ? sessionOrId?.id : sessionOrId;
    if (!sessionId) return;
    try {
      await deleteSession(sessionId);
      await refreshHubSessions();
      setMutationError(null);
    } catch (err) {
      console.error('Error deleting session:', err);
      setMutationError(err.message || 'Failed to delete session');
    }
  };

  const handleManageSignups = (sessionId) => {
    navigate(`/club/${clubId}/sessions/${sessionId}/signups`, {
      state: { ...location.state, fromStartSession: true },
    });
  };

  if (!currentUser) {
    return (
      <SessionsPageShell>
        <Card variant="default" size="medium" className="text-center">
          <Heading level={2} className="mb-4">Please Log In</Heading>
          <Text className="mb-6">You need to be logged in to start a session.</Text>
          <Button onClick={() => navigate('/')} variant="primary">
            Go to Login
          </Button>
        </Card>
      </SessionsPageShell>
    );
  }

  if (clubLoading) {
    return (
      <SessionsPageShell>
        <Card variant="default" size="medium" className="py-12 text-center">
          <Text>Loading club details...</Text>
        </Card>
      </SessionsPageShell>
    );
  }

  if (clubError || !hasPermission || !isAdmin) {
    return (
      <SessionsPageShell>
        <Card variant="default" size="medium" className="py-12 text-center">
          <Heading level={2} className="mb-4">Access Denied</Heading>
          <Text className="mb-6 text-text-secondary">
            {clubError || 'You do not have permission to start sessions for this club'}
          </Text>
          <Button onClick={() => navigate('/')} variant="primary">
            Go to Homepage
          </Button>
        </Card>
      </SessionsPageShell>
    );
  }

  return (
    <SeasonProvider clubId={clubId}>
      <SessionsPageShell>
        <div className="mb-6 sm:mb-8">
          <BackButton
            hideOnDesktop
            className="mb-2"
            onClick={() => {
              navigateBack(navigate, location, clubId, `/club-management/${clubId}`);
            }}
          >
            Back to Management
          </BackButton>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-3">
                {selectedClub ? (
                  <ClubLogo
                    logoImageUrl={selectedClub.logo_image_url}
                    clubName={selectedClub.name}
                    size="sm"
                    className="border border-border-medium shadow-brand-sm"
                  />
                ) : null}
                <Heading level={1} className="min-w-0 truncate text-xl font-bold text-text-primary sm:text-2xl">
                  Start session
                </Heading>
              </div>
              <Text className="mt-1 text-sm text-text-secondary">
                Start now, or start a session you already planned for {selectedClub?.name}
              </Text>
            </div>
            <Button
              onClick={() => setShowStartNowModal(true)}
              variant="primary"
              className="w-full shrink-0 sm:w-auto"
              disabled={startNowLoading}
            >
              Start new session
            </Button>
          </div>
        </div>

        {error ? (
          <Card variant="danger" size="medium" className="mb-6">
            <Text className="text-sm">{error}</Text>
          </Card>
        ) : null}

        {loading ? (
          <Text className="text-text-secondary">Loading sessions...</Text>
        ) : (
          <div className="space-y-6 sm:space-y-8">
            <SessionsTodayWork
              items={todayWork}
              includeTypes={['live', 'startable']}
              clubId={clubId}
              clubLogoUrl={selectedClub.logo_image_url}
              clubName={selectedClub.name}
              selectedClub={selectedClub}
              currentUser={currentUser}
              starterPlayerId={currentUser?.id}
              onStartSession={handleStartPlanned}
              onEditSession={handleEditSession}
              onUpdateSessionStatus={handleUpdateSessionStatus}
              onCancelSession={handleCancelSession}
              onDeleteSession={handleDeleteSession}
              onManageSignups={handleManageSignups}
              sectionTitle="Today"
            />

            <SessionsTodayWork
              items={otherPlannedItems}
              includeTypes={['startable']}
              clubId={clubId}
              clubLogoUrl={selectedClub.logo_image_url}
              clubName={selectedClub.name}
              selectedClub={selectedClub}
              currentUser={currentUser}
              starterPlayerId={currentUser?.id}
              onStartSession={handleStartPlanned}
              onEditSession={handleEditSession}
              onUpdateSessionStatus={handleUpdateSessionStatus}
              onCancelSession={handleCancelSession}
              onDeleteSession={handleDeleteSession}
              onManageSignups={handleManageSignups}
              sectionTitle="Other planned sessions"
              startableTitle="Start a planned session"
              startableDescription="Sessions you already created — start when you are ready"
            />
          </div>
        )}

        {showStartNowModal && selectedClub && (
          <CustomSessionConfigModal
            isOpen={showStartNowModal}
            onClose={handleCloseStartNowModal}
            onStartSession={handleStartNow}
            selectedClub={selectedClub}
            currentUser={currentUser}
          />
        )}
      </SessionsPageShell>
    </SeasonProvider>
  );
};

export default SessionStartPage;
